import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UnreadProvider } from "@/context/UnreadContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { PresenceProvider } from "@/context/PresenceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthBootstrap from "@/components/AuthBootstrap";
import { installUnsavedGuard } from "@/lib/unsavedGuard";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvitation from "@/pages/AcceptInvitation";
import VendorClaim from "@/pages/VendorClaim";
import VendorOnboarding from "@/pages/VendorOnboarding";
import VendorPortal from "@/pages/VendorPortal";
import Dashboard from "@/pages/Dashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import Chat from "@/pages/Chat";
import ClientPortal from "@/pages/ClientPortal";
import MyWork from "@/pages/MyWork";
import MyHR from "@/pages/MyHR";
import Studio from "@/pages/Studio";
import CRMShell from "@/pages/CRMShell";
import Approvals from "@/pages/Approvals";
import AdminRegistry from "@/pages/AdminRegistry";
import PasswordVault from "@/pages/PasswordVault";
import Notifications from "@/pages/Notifications";
import PendingApprovalScreen from "@/pages/PendingApprovalScreen";
import AccessManagement from "@/pages/AccessManagement";
import FeatureGate from "@/components/FeatureGate";
import { CareersIndex, CareersApply } from "@/pages/Careers";
import { Toaster } from "@/components/ui/sonner";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user._pending && (user.kind === "vendor" || user.role === "vendor")) return <Navigate to="/vendor/onboarding" replace />;
  // Non-vendor party still awaiting admin approval (status !== "active").
  // Without this branch, the `_pending` stub falls through to <Dashboard /> which
  // then redirect-loops via ProtectedRoute because the stub has no `role`.
  if (user._pending) return <PendingApprovalScreen />;
  if (user.role === "vendor") return <Navigate to="/vendor" replace />;
  if (user.role === "client") return <Navigate to="/client" replace />;
  return <Dashboard />;
}

function VendorOnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!(user.kind === "vendor" || user.role === "vendor")) return <Navigate to="/" replace />;
  if (user.status === "active") return <Navigate to="/vendor" replace />;
  return <VendorOnboarding />;
}

function VendorPortalRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "vendor") return <Navigate to={user.role === "client" ? "/client" : "/"} replace />;
  if (user.status && user.status !== "active") return <Navigate to="/vendor/onboarding" replace />;
  return <VendorPortal />;
}

// Phase 79 — `founder` is a superset of admin. Added to every internal / cm
// role list so it can access all admin-gated pages without touching each guard.
const internalRoles = ["founder", "admin", "client_manager", "designer", "engineer", "strategist", "operations", "intern"];
const clientRoles = ["client"];
const cmRoles = ["founder", "admin", "client_manager"];

// Redirects from old flat routes -> new hub+tab URLs
const REDIRECTS = [
  ["projects",     "/my-work?tab=projects"],
  ["reviews",      "/my-work?tab=reviews"],
  ["goals",        "/my-work?tab=goals"],
  ["timesheet",    "/my-work?tab=timesheet"],
  ["attendance",   "/my-work?tab=attendance"],
  ["leaves",       "/my-hr?tab=leaves"],
  ["performance",  "/my-hr?tab=performance"],
  ["hr",           "/my-hr?tab=documents"],
  ["grievances",   "/my-hr?tab=grievances"],
  ["profile",      "/my-hr?tab=profile"],
  ["finance",      "/studio?tab=finance&sub=ledger"],
  ["sources",      "/studio?tab=finance&sub=sources"],
  ["purchases",    "/studio?tab=finance&sub=purchases"],
  ["assets",       "/studio?tab=finance&sub=assets"],
  ["vendors",      "/studio?tab=finance&sub=vendors"],
  ["clients",      "/crm?tab=clients"],
  ["contracts",    "/crm?tab=contracts"],
  ["users",        "/my-hr?tab=team"],
  ["recruitment",  "/my-hr?tab=recruitment"],
  ["audit",        "/studio?tab=audit"],
];

export default function App() {
  installUnsavedGuard();
  return (
    <AuthProvider>
      <PresenceProvider>
      <UnreadProvider>
      <NotificationsProvider>
        <BrowserRouter>
          <AuthBootstrap />
          <Toaster richColors position="top-center" duration={1800} />
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/vendor-claim/:token" element={<VendorClaim />} />
            <Route path="/careers" element={<CareersIndex />} />
            <Route path="/careers/:roleId" element={<CareersApply />} />
            <Route path="/our-studio" element={<Navigate to="/careers" replace />} />

            {/* Vendor portal — onboarding accepts any non-active vendor, portal requires status=active */}
            <Route path="/vendor/onboarding" element={<VendorOnboardingRoute />} />
            <Route path="/vendor/*" element={<VendorPortalRoute />} />

            {/* Client portal */}
            <Route path="/client/*" element={
              <ProtectedRoute allow={clientRoles}><ClientPortal /></ProtectedRoute>
            } />

            {/* Internal portal */}
            <Route element={
              <ProtectedRoute allow={internalRoles}><Layout /></ProtectedRoute>
            }>
              <Route index element={<RootRedirect />} />

              {/* 4 hubs */}
              <Route path="my-work" element={<MyWork />} />
              <Route path="my-hr"   element={<MyHR />} />
              <Route path="studio"  element={
                <FeatureGate anyFeature={["studio_view", "finance", "studio_taxonomy", "audit_log"]} label="Studio"><Studio /></FeatureGate>
              } />
              <Route path="crm" element={
                <FeatureGate feature="crm_access" label="CRM"><CRMShell /></FeatureGate>
              } />
              <Route path="access-management" element={<AccessManagement />} />
              <Route path="approvals" element={
                <Navigate to="/my-work?tab=reviews" replace />
              } />
              <Route path="reviews/approvals" element={
                <Navigate to="/my-work?tab=reviews" replace />
              } />

              {/* Chat — reachable from the top-right icon on every page */}
              <Route path="chat" element={
                <FeatureGate feature="chat_view" label="Chat"><Chat /></FeatureGate>
              } />

              {/* Notifications — global activity feed, reachable from every page */}
              <Route path="notifications" element={<Notifications />} />

              {/* Project detail keeps its own page (linked from Projects tab) */}
              <Route path="projects/:id" element={<ProjectDetail />} />

              {/* Feb 2026 · Review detail lives inline in the Approvals modal
                  now. The old flat /reviews/:id page was removed as part of
                  Task #7 (batch UI/UX refresh). Any lingering deep-links from
                  emails / older bookmarks get redirected to the inbox rather
                  than 404-ing. */}
              <Route path="reviews/:id" element={<Navigate to="/my-work?tab=reviews" replace />} />

              {/* Admin Registry — single source of truth (admin/hr only, or anyone granted Manage Public Branding) */}
              <Route path="admin/registry" element={
                <FeatureGate anyFeature={["registry_view", "branding_admin"]} label="Registry"><AdminRegistry /></FeatureGate>
              } />

              {/* Password Vault — owner + shared visibility (all internal users) */}
              <Route path="vault" element={
                <FeatureGate feature="vault_view" label="Vault"><PasswordVault /></FeatureGate>
              } />

              {/* Backwards-compat redirects from old flat routes */}
              {REDIRECTS.map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </NotificationsProvider>
      </UnreadProvider>
      </PresenceProvider>
    </AuthProvider>
  );
}
